<template>
  <div data-view="contracts">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame :title="title">
      <template v-slot:header-actions>
        <Can action="store" subject="contract">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'contracts-new', query: $route.query })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_contract') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:content>
        <ContractsFilters @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :items="contracts"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectContract"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
          :on-change-page="onChangePage"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'status.name'">
              <StatusPanel
                table-group="contracts"
                :status="scope.cell.item.status.name"
              />
            </template>
            <template v-else-if="scope.cell.column.value === 'owner'">
              {{ scope.cell.item.owner.name }}
            </template>
            <template v-else-if="scope.cell.column.value === 'expire_at'">
              {{ expireAtValue(scope.cell.item, scope.cell.column.value) }}
            </template>
            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import ContractsFilters from '../../components/forms/filters/Contract';
  import urlPaginationRestore from '@/mixins/urlPaginationRestore';
  import StatusPanel from '../../components/panels/Status';
  import { isNull, isStr, extractNestedProp } from '../../utils';
  import columns from '../../config/contracts/columns';

  export default {
    mixins: [urlPaginationRestore],
    components: {
      ContractsFilters,
      StatusPanel,
    },
    data() {
      return {
        columns,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'expire_at',
          totalItems: 0,
        },
        totalPages: 0,
        query: { per_page: 15, expire_soon: 0 },
        isLoading: false,
      };
    },
    computed: {
      title() {
        const amount = this.totalCount > 0 ? `(${this.totalCount})` : '';
        return this.$t('contracts') + ' ' + amount;
      },
      contracts() {
        return this.$store.getters['contracts/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      contractTypes() {
        return this.$store.getters['contracts/contractTypes'];
      },
      paymentTypes() {
        return this.$store.getters['contracts/paymentTypes'];
      },
    },
    created() {
      if (this.contractTypes.length === 0) {
        this.getContractTypes();
      }

      if (this.paymentTypes.length === 0) {
        this.getPaymentTypes();
      }
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getContracts(this.query);
      },
      getContracts(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('contracts/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['contracts/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectContract(contract) {
        this.$store.dispatch('contracts/setActiveItem', contract);
        this.$router.push({ name: 'contracts-edit', params: { id: contract.id }, query: this.$route.query });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;
        this.runInitialFetch(() => this.getContracts(nextQuery));
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;
        this.getContracts(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.runInitialFetch(() => this.getContracts(this.query));
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, per_page: 15, page: 1, expire_soon: 0 };

        this.getContracts(this.query);
      },
      getContractTypes() {
        return this.$store.dispatch('contracts/getContractTypes');
      },
      getPaymentTypes() {
        return this.$store.dispatch('contracts/getPaymentTypes');
      },
      expireAtValue(item, columnValue) {
        if (isNull(item.expire_at)) {
          return 'Unlimited';
        }
        return extractNestedProp(item, columnValue);
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

[data-view='contracts'] {
  .trigger.toggle-menu {
    margin-right: 0;
  }

  .v-datatable {
    min-width: $table-min-width;
  }
}
</style>
